import {
  Box,
  CardActionArea,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";

import type { SelectedProduct } from "@/features/Generations/context/PilotStateContext";
import type { ProductCardsTaxonomyFilter } from "@/types/ingestedProduct";

import { useCatalogSearch, useInfiniteListProductCards } from "@/api/products";
import { PurchasedProductCard } from "@/features/Profiles/components/PurchasedProductCard";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { CloseIcon, SearchIcon } from "@/icons";

const SEARCH_DEBOUNCE_MS = 300;

export interface ProductPickerModalProps {
  open: boolean;
  siteId: string;
  onClose: () => void;
  onSelect: (product: SelectedProduct) => void;
  filter?: ProductCardsTaxonomyFilter;
}

/**
 * Modal for picking the pilot product. Opens on an unfiltered catalog search;
 * when a `filter` is supplied it lists that SKU Store source's products
 * instead, with the search field narrowing them client-side.
 */
export function ProductPickerModal({
  open,
  siteId,
  onClose,
  onSelect,
  filter,
}: ProductPickerModalProps) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const scoped = filter !== undefined;

  const {
    data: searchData,
    isFetching: isSearchFetching,
    isError: isSearchError,
  } = useCatalogSearch(siteId, debouncedSearch.trim(), {
    enabled: Boolean(siteId) && open && !scoped,
  });

  const {
    data: listData,
    isLoading: isListLoading,
    isError: isListError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteListProductCards(siteId, {
    filter,
    enabled: Boolean(siteId) && open && scoped,
  });

  // Load the next page when the sentinel scrolls into view. The scoped list is
  // filtered client-side, so keep paging while a search is active too —
  // otherwise matches past page 1 stay unreachable.
  const { ref: loadMoreRef, inView } = useInView();
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const searchProducts = useMemo(
    () =>
      (searchData?.results ?? [])
        .filter((item) => item.vendor_product_id.trim().length > 0)
        .map((item) => ({
          id: item.vendor_product_id.trim(),
          title: item.title?.trim() || item.vendor_product_id.trim(),
          image_url: item.image_url,
        })),
    [searchData?.results],
  );

  const scopedProducts = useMemo(() => {
    const cards = listData?.pages.flatMap((page) => page.products) ?? [];
    const mapped = cards
      .filter((card) => card.product_id.trim().length > 0)
      .map((card) => ({
        id: card.product_id.trim(),
        title: card.title?.trim() || card.product_id.trim(),
        image_url: card.image_url,
      }));
    const trimmed = debouncedSearch.trim().toLowerCase();
    if (!trimmed) return mapped;
    return mapped.filter(
      (product) =>
        product.title.toLowerCase().includes(trimmed) ||
        product.id.toLowerCase().includes(trimmed),
    );
  }, [listData?.pages, debouncedSearch]);

  const products = scoped ? scopedProducts : searchProducts;
  const isFetching = scoped ? isListLoading : isSearchFetching;
  const isError = scoped ? isListError : isSearchError;

  const handleClose = () => {
    setSearch("");
    onClose();
  };

  const handleSelect = (product: SelectedProduct) => {
    onSelect(product);
    handleClose();
  };

  const hasResults = products.length > 0;
  const showLoadMore = scoped && Boolean(hasNextPage) && !isError;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      sx={{ "& .MuiDialog-paper": { maxWidth: 1024 } }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography variant="h6" component="span">
            Choose a product
          </Typography>
          <IconButton
            aria-label="Close"
            size="small"
            onClick={handleClose}
            sx={{ flexShrink: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          fullWidth
          size="small"
          autoFocus
          placeholder="Search products by name"
          sx={{ mb: 4 }}
          slotProps={{
            htmlInput: {
              "aria-label": "Search products",
              "data-testid": "product-picker-search-input",
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon aria-hidden fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />

        {isFetching && !hasResults ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 10,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : isError ? (
          <Box sx={{ py: 10, textAlign: "center" }}>
            <Typography variant="body2" color="error">
              Couldn&apos;t load products. Try again.
            </Typography>
          </Box>
        ) : hasResults ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
              },
              gap: 3,
            }}
          >
            {products.map((product) => (
              <CardActionArea
                key={product.id}
                onClick={() => handleSelect(product)}
                data-testid={`product-picker-option-${product.id}`}
                sx={{
                  width: "100%",
                }}
              >
                <PurchasedProductCard
                  imageSrc={product.image_url}
                  imageAlt={product.title}
                  name={product.title}
                  subtitle={""}
                />
              </CardActionArea>
            ))}
          </Box>
        ) : showLoadMore ? null : (
          <Box sx={{ py: 10, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {debouncedSearch.trim()
                ? "No products match your search."
                : "No products found."}
            </Typography>
          </Box>
        )}

        {showLoadMore && (
          <Box
            ref={loadMoreRef}
            data-testid="product-picker-load-more"
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 3,
              minHeight: 40,
            }}
          >
            {isFetchingNextPage && <CircularProgress size={24} />}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
